import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import {
  ChemistryResultResponse,
  ProcessPhaseResponse,
  ProcessResponse,
} from "../models/eaf.models";
import { PourbackResponse } from "../models/ms.models";

const moment = getMomentTz();

export const getEAFProcess = async ({
  ProcessId = null,
}: {
  ProcessId: number | null;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2("[EAF].[DEGetProcess]", [
    { name: "ProcessId", value: ProcessId },
  ]);

  if (resp.ok) {
    const data = resp.data as ProcessResponse;

    const process = data.tables[0].rows[0];
    data.tables[0].rows[0] = {
      ...data.tables[0].rows[0],
      Start: moment.utc(process["Start"]).toDate(),
      StartHeat: moment.utc(process["StartHeat"]).toDate(),
      End: process.End ? moment.utc(process["End"]).toDate() : null,
      StartTap: process.StartTap
        ? moment.utc(process["StartTap"]).toDate()
        : null,
      ProductionDate: process.ProductionDate
        ? moment.utc(process["ProductionDate"]).toDate()
        : null,
      IsNonFreeOpenModifiedAt: process.IsNonFreeOpenModifiedAt
        ? moment.utc(process["IsNonFreeOpenModifiedAt"]).toDate()
        : null,
    };
    data.tables[1].rows = data.tables[1].rows.map((row) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
    data.tables[2].rows = data.tables[2].rows.map((row) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
    data.tables[3].rows = data.tables[3].rows.map((row) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
    data.tables[4].rows = data.tables[4].rows.map((row) => {
      row.Start = moment.utc(row["Start"]).toDate();
      row.End = moment.utc(row["End"]).toDate();
      return row;
    });
    data.tables[5].rows = data.tables[5].rows.map((row) => {
      row.SourceTime = moment.utc(row["SourceTime"]).toDate();
      row.ChargedTime = moment.utc(row["ChargedTime"]).toDate();
      return row;
    });
    data.tables[6].rows = data.tables[6].rows.map((row) => {
      row.Time = moment.utc(row["Time"]).toDate();
      return row;
    });
    data.tables[8].rows = data.tables[8].rows.map((row) => {
      row.StartTime = moment.utc(row["StartTime"]).toDate();
      row.EndTime = moment.utc(row["EndTime"]).toDate();
      return row;
    });

    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export const getPhases = async ({
  ProcessId = null,
}: {
  ProcessId: number | null;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2("[EAF].[GetPhasesByProcess]", [
    { name: "ProcessId", value: ProcessId },
  ]);
  if (resp.ok) {
    const data = resp.data as ProcessPhaseResponse;

    data.tables[0].rows.map((phase) => {
      phase.Start = moment.utc(phase["Start"]).toDate();
      phase.End = phase.End ? moment.utc(phase["End"]).toDate() : null;
    });

    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message!,
    };
  }
};

export type UpsertProcessParams = {
  ProcessId: number | null;
  Start: string;
  End: string | null;
};

export const upsertProcess = async ({
  ProcessId,
  Start,
  End,
}: UpsertProcessParams) => {
  const apiService = new MESApiService();

  const parameters = [
    { name: "ProcessId", value: ProcessId },
    { name: "Start", value: Start },
    { name: "End", value: End },
    { name: "IsManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertProcess]", parameters);
};

interface UpsertProcessEndParams {
  ProcessId: number;
  End: string | null;
}

export const upsertProcessEnd = async ({
  ProcessId,
  End,
}: UpsertProcessEndParams) => {
  const apiService = new MESApiService();

  const parameters = [
    { name: "ProcessId", value: ProcessId },
    { name: "End", value: End },
  ];

  return await apiService.callV2("[EAF].[MoveEndTapBoundary]", parameters);
};

type UpsertProcessDataParams = {
  processId: number;
  field: string;
  value: boolean | string | number | Date | null;
};

export const upsertProcessData = async ({
  processId,
  field,
  value,
  extrafield,
  extravalue,
}: UpsertProcessDataParams & {
  extrafield?: string;
  extravalue?: boolean | string | number | Date | null;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: processId },
    { name: field, value: value },
    { name: "IsManual", value: true },
  ];
  if (extrafield && extravalue !== undefined) {
    parameters.push({ name: extrafield, value: extravalue });
  }
  return await apiService.callV2("[EAF].[UpsertProcessData]", parameters);
};

type UpsertChargeParams = {
  ChargeId: number | null;
  ProcessId: number;
  Timestamp: string;
  BucketId: number;
  Weight: number;
  Lime: number;
  IsChargeManual?: boolean;
  IsNotFit?: boolean;
};

export const upsertCharge = async ({
  ChargeId,
  ProcessId,
  Timestamp,
  Weight,
  Lime,
  BucketId,
  IsChargeManual = true,
  IsNotFit,
}: UpsertChargeParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ChargeId", value: ChargeId },
    { name: "ProcessId", value: ProcessId },
    { name: "Timestamp", value: Timestamp },
    { name: "Weight", value: Weight },
    { name: "Lime", value: Lime },
    { name: "BucketId", value: BucketId },
    { name: "IsChargeManual", value: IsChargeManual },
    { name: "IsNotFit", value: IsNotFit ?? null },
  ];
  return await apiService.callV2("[EAF].[UpsertCharge]", parameters);
};

type UpsertProbeParams = {
  ProbeId: number | null;
  ProcessId: number;
  Timestamp: string;
  Temperature: number | null;
  Carbon: number | null;
  Oxygen: number | null;
  MWH: number | null;
  IsProbeManual: boolean;
};

export const upsertProbe = async ({
  ProbeId,
  ProcessId,
  Timestamp,
  Temperature,
  Carbon,
  Oxygen,
  MWH,
  IsProbeManual,
}: UpsertProbeParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProbeId", value: ProbeId },
    { name: "ProcessId", value: ProcessId },
    { name: "Timestamp", value: Timestamp },
    { name: "Temperature", value: Temperature },
    { name: "Carbon", value: Carbon },
    { name: "Oxygen", value: Oxygen },
    { name: "MWH", value: MWH },
    { name: "IsProbeManual", value: IsProbeManual },
  ];

  return await apiService.callV2("[EAF].[UpsertProbe]", parameters);
};

type UpsertHeatParams = {
  HeatId: number;
  field: string;
  value: any;
};

export const upsertHeat = async ({
  HeatId,
  field,
  value,
}: UpsertHeatParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatId", value: HeatId },
    { name: field, value: value },
  ];

  return await apiService.callV2("[MS].[UpsertHeat]", parameters);
};

type UpsertAdditionParams = {
  AdditionId: number;
  ProcessId: number;
  Timestamp: string;
  Weight: number;
  MaterialId: number;
  Source: string | null;
};

export const UpsertAddition = async ({
  AdditionId,
  ProcessId,
  Timestamp,
  Weight,
  MaterialId,
  Source,
}: UpsertAdditionParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "AdditionId", value: AdditionId },
    { name: "ProcessId", value: ProcessId },
    { name: "Timestamp", value: Timestamp },
    { name: "Weight", value: Weight },
    { name: "MaterialId", value: MaterialId },
    { name: "Source", value: Source },
    { name: "IsAdditionManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertAddition]", parameters);
};

type UpsertHeatingParams = {
  HeatingId: number;
  ProcessId: number;
  StartTime: string;
  EndTime: string;
  Energy: number;
};

export const UpsertHeating = async ({
  HeatingId,
  ProcessId,
  StartTime,
  EndTime,
  Energy,
}: UpsertHeatingParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatingId", value: HeatingId },
    { name: "ProcessId", value: ProcessId },
    { name: "StartTime", value: StartTime },
    { name: "EndTime", value: EndTime },
    { name: "Energy", value: Energy },
    { name: "IsHeatingManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertHeating]", parameters);
};

type UpsertPourbackParams = {
  PourbackId: number;
  HeatId: number;
  RemainingWeight: number | null;
  DestinationProcessId: number;
  ChargedTime: string;
  ChargedWeight: number;
  Comments: string;
};

export const UpsertPourbackCharge = async ({
  PourbackId,
  HeatId,
  RemainingWeight,
  DestinationProcessId,
  ChargedTime,
  ChargedWeight,
  Comments,
}: UpsertPourbackParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "PourbackId", value: PourbackId },
    { name: "HeatId", value: HeatId },
    { name: "RemainingWeight", value: RemainingWeight },
    { name: "DestinationProcessId", value: DestinationProcessId },
    { name: "ChargedTime", value: ChargedTime },
    { name: "ChargedWeight", value: ChargedWeight },
    { name: "Comments", value: Comments },
  ];

  return await apiService.callV2("[MS].[UpsertPourback]", parameters);
};

type upsertInjectionParams = {
  ProcessId: number;
  InjectionTypeId: number;
  Injector1: number | null;
  Injector2: number | null;
  Injector3: number | null;
  InjectorDoor: number | null;
};

export const upsertInjection = async ({
  ProcessId,
  InjectionTypeId,
  Injector1,
  Injector2,
  Injector3,
  InjectorDoor,
}: upsertInjectionParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "InjectionTypeId", value: InjectionTypeId },
    { name: "Injector1", value: Injector1 },
    { name: "Injector2", value: Injector2 },
    { name: "Injector3", value: Injector3 },
    { name: "InjectorDoor", value: InjectorDoor },
    { name: "IsInjectionManual", value: true },
  ];

  return await apiService.callV2("[EAF].[UpsertProcessInjections]", parameters);
};

export const readPhasesFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2("[RBD].[ReadEAFPhasesFromPLC]", parameters);
};

export const readProbesFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2("[RBD].[ReadEAFProbesFromPLC]", parameters);
};

export const readChargesFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2("[RBD].[ReadEAFChargesFromPLC]", parameters);
};

export const readAdditionsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2("[RBD].[ReadEAFAdditionsFromPLC]", parameters);
};

export const readHeatingsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2("[RBD].[ReadEAFHeatingsFromPLC]", parameters);
};

export const readInjectionsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Force", value: true },
  ];

  return await apiService.callV2(
    "[RBD].[ReadEAFInjectionsFromPLC]",
    parameters,
  );
};

export const deleteProbeById = async ({ ProbeId }: { ProbeId: number }) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProbeId", value: ProbeId }];

  return await apiService.callV2("[EAF].[DeleteProbe]", parameters);
};

export const deleteChargeById = async ({ ChargeId }: { ChargeId: number }) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ChargeId", value: ChargeId }];

  return await apiService.callV2("[EAF].[DeleteCharge]", parameters);
};

export const deleteAdditionById = async ({
  AdditionId,
}: {
  AdditionId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "AdditionId", value: AdditionId }];

  return await apiService.callV2("[EAF].[DeleteAddition]", parameters);
};

export const deleteHeatingById = async ({
  HeatingId,
}: {
  HeatingId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "HeatingId", value: HeatingId }];

  return await apiService.callV2("[EAF].[DeleteHeating]", parameters);
};

export const deletePourbackChargeById = async ({
  PourbackId,
}: {
  PourbackId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "PourbackId", value: PourbackId }];

  return await apiService.callV2("[MS].[DeletePourbackCharge]", parameters);
};

export const getLastestProcesses = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessCode", value: "E" }];
  return await apiService.callV2("[RBD].[GetLastestNProcesses]", parameters);
};

export const getPourbackHeats = async (
  showMoreHeats: boolean,
  heatId: number,
) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "IncludeAllHeats", value: showMoreHeats },
    { name: "HeatId", value: heatId },
  ];

  return await apiService.callV2("[MS].[GetPourbackHeats]", parameters);
};

export const getPourbacks = async ({
  ProcessId = null,
}: {
  ProcessId: number | null;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2("[MS].[GetPourbackCharges]", [
    { name: "ProcessId", value: ProcessId },
  ]);
  if (resp.ok) {
    const data = resp.data as PourbackResponse;

    data.tables[0].rows.map((pourback) => {
      pourback.SourceTime = moment.utc(pourback["SourceTime"]).toDate();
      pourback.ChargedTime = pourback.ChargedTime
        ? moment.utc(pourback["ChargedTime"]).toDate()
        : null;
    });

    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message!,
    };
  }
};

export const getChemistries = async ({
  ProcessId = null,
}: {
  ProcessId: number | null;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2("[LAB].[GetChemistryResult]", [
    { name: "ProcessId", value: ProcessId },
  ]);
  if (resp.ok) {
    const data = resp.data as ChemistryResultResponse;

    data.tables[0].rows.map((chemistry) => {
      chemistry.Time = moment.utc(chemistry["Time"]).toDate();
    });

    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message!,
    };
  }
};

export const searchProcesses = async (searchTerm: string) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "SearchTerm", value: searchTerm },
    { name: "MaxResults", value: 100 },
    { name: "AssetId", value: "11" },
  ];

  return await apiService.callV2("[RBD].[SearchProcesses]", parameters);
};
